import React from 'react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-extrabold text-orange-400 mb-4">
            Privacy Policy
          </h1>
          <p className="text-gray-300 text-sm">
            Last Updated: December 18, 2024
          </p>
        </div>

        {/* Content Card */}
        <div className="bg-gray-800 rounded-lg shadow-lg p-8 mb-8">
          <div className="prose prose-invert max-w-none">
            
            {/* Legal Notice */}
            <div className="bg-orange-900 border border-orange-700 text-orange-200 px-4 py-3 rounded-md mb-8">
              <p className="text-sm font-medium">
                <strong>Important Legal Notice:</strong> This is a template document provided for reference purposes only. 
                This content is not a substitute for professional legal advice. You should consult with qualified legal 
                and privacy professionals before using this content in any production environment.
              </p>
            </div>

            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-orange-400 mb-4">Introduction</h2>
              <div className="text-gray-300 space-y-4">
                <p>
                  Bitcoin Wealth Builder ("we", "our", or "us") respects your privacy and is committed to protecting 
                  your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your 
                  information when you use our web application and services.
                </p>
                <p>
                  By using Bitcoin Wealth Builder, you consent to the data practices described in this Privacy Policy. 
                  If you do not agree with the practices described in this policy, please do not use our services.
                </p>
              </div>
            </section>

            {/* 1. Information We Collect */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-orange-400 mb-4">1. Information We Collect</h2>
              <div className="text-gray-300 space-y-4">
                
                <h3 className="text-lg font-semibold text-orange-300">Personal Information</h3>
                <p>When you create an account with us, we collect:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Email Address:</strong> Required for account creation, authentication, and communication</li>
                  <li><strong>Password:</strong> Encrypted and stored securely for account access</li>
                  <li><strong>Account Metadata:</strong> Creation date, last login, and account status</li>
                </ul>

                <h3 className="text-lg font-semibold text-orange-300 mt-6">Financial Planning Data</h3>
                <p>When you use our calculators and planning tools, we may store:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Calculator Inputs:</strong> Investment amounts, time horizons, interest rates, and other parameters you enter</li>
                  <li><strong>Saved Goals:</strong> Your financial goals, targets, and planning scenarios</li>
                  <li><strong>Bitcoin Addresses:</strong> Wallet addresses you choose to track (stored securely)</li>
                  <li><strong>Generated Reports:</strong> AI-generated financial reports based on your calculations</li>
                  <li><strong>Usage Patterns:</strong> Which calculators you use and when, to improve our services</li>
                </ul>

                <h3 className="text-lg font-semibold text-orange-300 mt-6">Technical Information</h3>
                <p>We automatically collect certain technical information:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Device Information:</strong> Browser type, operating system, and device identifiers</li>
                  <li><strong>Usage Data:</strong> Pages visited, time spent, and feature usage</li>
                  <li><strong>IP Address:</strong> For security and analytics purposes</li>
                  <li><strong>Cookies:</strong> Essential cookies for authentication and session management</li>
                </ul>
              </div>
            </section>

            {/* 2. How We Use Your Information */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-orange-400 mb-4">2. How We Use Your Information</h2>
              <div className="text-gray-300 space-y-4">
                <p>We use the information we collect for the following purposes:</p>
                
                <h3 className="text-lg font-semibold text-orange-300">Service Provision</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>To create and manage your user account</li>
                  <li>To provide access to our financial calculators and planning tools</li>
                  <li>To save and retrieve your calculations and goals</li>
                  <li>To generate AI-powered financial reports</li>
                  <li>To track Bitcoin wallet balances (if you choose to use this feature)</li>
                </ul>

                <h3 className="text-lg font-semibold text-orange-300 mt-6">Communication</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>To send account-related notifications and security alerts</li>
                  <li>To respond to your inquiries and support requests</li>
                  <li>To provide important updates about our services</li>
                </ul>

                <h3 className="text-lg font-semibold text-orange-300 mt-6">Security and Legal</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>To verify your identity and prevent unauthorized access</li>
                  <li>To detect and prevent fraud, abuse, and security incidents</li>
                  <li>To comply with legal obligations and protect our legal rights</li>
                </ul>
              </div>
            </section>

            {/* 3. Data Storage and Security */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-orange-400 mb-4">3. Data Storage and Security</h2>
              <div className="text-gray-300 space-y-4">
                <div className="bg-blue-900 border border-blue-700 text-blue-200 px-4 py-3 rounded-md">
                  <p className="text-sm font-medium">
                    <strong>Secure Infrastructure:</strong> Your data is stored securely using Supabase, a modern 
                    backend-as-a-service platform that provides enterprise-grade security, encryption, and compliance features.
                  </p>
                </div>
                
                <h3 className="text-lg font-semibold text-orange-300">Security Measures</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Encryption:</strong> All data is encrypted in transit using HTTPS and at rest using industry-standard encryption</li>
                  <li><strong>Access Controls:</strong> Strict access controls ensure only you can access your data</li>
                  <li><strong>Row Level Security:</strong> Database-level security policies prevent unauthorized data access</li>
                  <li><strong>Authentication:</strong> Secure authentication powered by Supabase Auth</li>
                  <li><strong>Regular Security Updates:</strong> We keep our systems updated with the latest security patches</li>
                </ul>

                <h3 className="text-lg font-semibold text-orange-300 mt-6">Data Location</h3>
                <p>
                  Your data is stored in secure data centers operated by Supabase. For specific information about 
                  data center locations and compliance certifications, please refer to Supabase's security documentation.
                </p>
              </div>
            </section>

            {/* 4. Information Sharing */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-orange-400 mb-4">4. Information Sharing</h2>
              <div className="text-gray-300 space-y-4">
                <div className="bg-green-900 border border-green-700 text-green-200 px-4 py-3 rounded-md">
                  <p className="text-sm font-medium">
                    <strong>We Do Not Sell Your Data:</strong> We do not sell, trade, or otherwise transfer your 
                    personal information to third parties for marketing purposes.
                  </p>
                </div>

                <p>We may share your information only in the following limited circumstances:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Service Providers:</strong> With trusted third parties (like Supabase) who help us provide our services</li>
                  <li><strong>Legal Requirements:</strong> When required by law, court order, or government request</li>
                  <li><strong>Safety and Security:</strong> To protect our users, prevent fraud, or ensure service security</li>
                  <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets (with notice)</li>
                </ul>
              </div>
            </section>

            {/* 5. Your Rights and Choices */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-orange-400 mb-4">5. Your Rights and Choices</h2>
              <div className="text-gray-300 space-y-4">
                <p>You have the following rights regarding your personal data:</p>
                
                <h3 className="text-lg font-semibold text-orange-300">Access and Control</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Access:</strong> Request access to the personal data we have about you</li>
                  <li><strong>Correction:</strong> Update or correct inaccurate personal information</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal data (subject to legal requirements)</li>
                  <li><strong>Data Portability:</strong> Request a copy of your data in a portable format</li>
                </ul>

                <h3 className="text-lg font-semibold text-orange-300 mt-6">Account Management</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Account Deletion:</strong> You can delete your account at any time through account settings</li>
                  <li><strong>Data Export:</strong> Export your saved calculations and goals before account deletion</li>
                  <li><strong>Communication Preferences:</strong> Control what communications you receive from us</li>
                </ul>
              </div>
            </section>

            {/* 6. Third-Party Services */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-orange-400 mb-4">6. Third-Party Services</h2>
              <div className="text-gray-300 space-y-4">
                <p>Bitcoin Wealth Builder integrates with the following third-party services:</p>
                
                <h3 className="text-lg font-semibold text-orange-300">Supabase (Data Storage)</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Purpose: Secure data storage, authentication, and database services</li>
                  <li>Data Shared: All user data is stored through Supabase's secure infrastructure</li>
                  <li>Privacy Policy: <span className="text-orange-400">https://supabase.com/privacy</span></li>
                </ul>

                <h3 className="text-lg font-semibold text-orange-300 mt-6">AI Services (Report Generation)</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Purpose: Generate personalized financial reports and insights</li>
                  <li>Data Shared: Your calculation data is processed to create AI-generated reports</li>
                  <li>Processing: Data is processed securely and not stored by AI providers</li>
                </ul>

                <h3 className="text-lg font-semibold text-orange-300 mt-6">CoinGecko API (Bitcoin Prices)</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Purpose: Fetch current Bitcoin prices for LTV calculations</li>
                  <li>Data Shared: No personal data is shared; only price data is retrieved</li>
                  <li>Privacy Policy: <span className="text-orange-400">https://www.coingecko.com/en/privacy</span></li>
                </ul>
              </div>
            </section>

            {/* 7. Data Retention */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-orange-400 mb-4">7. Data Retention</h2>
              <div className="text-gray-300 space-y-4">
                <p>We retain your information for as long as necessary to provide our services and fulfill legal obligations:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Account Data:</strong> Retained while your account is active</li>
                  <li><strong>Financial Data:</strong> Retained as long as you choose to keep your goals and calculations</li>
                  <li><strong>Technical Data:</strong> Typically retained for 12-24 months for security and analytics</li>
                  <li><strong>Legal Requirements:</strong> Some data may be retained longer to comply with legal obligations</li>
                </ul>
                <p className="mt-4">
                  When you delete your account, we will permanently delete your personal data within 30 days, 
                  except where retention is required by law.
                </p>
              </div>
            </section>

            {/* 8. Children's Privacy */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-orange-400 mb-4">8. Children's Privacy</h2>
              <div className="text-gray-300 space-y-4">
                <p>
                  Bitcoin Wealth Builder is not intended for children under 13 years of age. We do not knowingly 
                  collect personal information from children under 13. If you are a parent or guardian and believe 
                  your child has provided us with personal information, please contact us immediately.
                </p>
              </div>
            </section>

            {/* 9. International Users */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-orange-400 mb-4">9. International Users</h2>
              <div className="text-gray-300 space-y-4">
                <p>
                  Bitcoin Wealth Builder is operated from various locations and may be accessed globally. 
                  If you are accessing our service from outside your country, please be aware that your 
                  information may be transferred to, stored, and processed in different jurisdictions.
                </p>
                <p>
                  By using our service, you consent to the transfer of your information to facilities and 
                  servers that may be located outside your country of residence.
                </p>
              </div>
            </section>

            {/* 10. Changes to Privacy Policy */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-orange-400 mb-4">10. Changes to This Privacy Policy</h2>
              <div className="text-gray-300 space-y-4">
                <p>
                  We may update this Privacy Policy from time to time to reflect changes in our practices or for 
                  other operational, legal, or regulatory reasons. We will notify you of any material changes by:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Posting the updated policy on our website</li>
                  <li>Updating the "Last Updated" date at the top of this policy</li>
                  <li>Sending you an email notification for significant changes</li>
                </ul>
                <p>
                  Your continued use of our service after any changes constitutes acceptance of the updated Privacy Policy.
                </p>
              </div>
            </section>

            {/* 11. Contact Us */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-orange-400 mb-4">11. Contact Us</h2>
              <div className="text-gray-300 space-y-4">
                <p>
                  If you have any questions about this Privacy Policy, your data, or our privacy practices, 
                  please contact us through the appropriate channels provided in the application.
                </p>
                <p>
                  We are committed to resolving any privacy-related concerns promptly and transparently.
                </p>
              </div>
            </section>
          </div>
        </div>

        {/* Navigation */}
        <div className="text-center space-y-4">
          <button
            onClick={() => navigate('/terms')}
            className="text-orange-400 hover:text-orange-300 text-sm font-medium transition-colors duration-200 mr-6"
          >
            View Terms of Service →
          </button>
          <button
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-gray-300 text-sm transition-colors duration-200"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;